import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Paperclip } from "lucide-react";
import SearchRequestStatusChip from "./SearchRequestStatusChip";
import { usePermission } from "../../hooks/usePermission";
import {
  formatRequestTitle,
  formatRequestMeta,
  formatTerms,
  formatSentDate,
} from "../../utils/searchRequestFormat";

// Compact LIST-view row for the N3 "My search requests" overview (C16), the row
// counterpart of SearchRequestCard. Shows the identity (role/position + age),
// meta (type · window · level), a terms indicator, sent-date, per-agent status
// chips, and the same overflow "Close request" (permission-gated). The whole row
// opens the detail; the overflow is guarded with stopPropagation.
const SearchRequestListRow = ({ request, onClose }) => {
  const navigate = useNavigate();
  const canClose = usePermission("delete_tenders");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const meta = formatRequestMeta(request);
  const terms = formatTerms(request);
  const sent = formatSentDate(request);
  const isClosed = request.status === "closed";
  const recipients = request.recipients || [];

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const openDetail = () => navigate(`/network/trusted-agent/${request.id}`);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openDetail();
        }
      }}
      className="bg-white border border-[#E5E7EB] rounded-lg px-4 py-3 flex items-center gap-4 cursor-pointer hover:border-[#D6DBE3] hover:shadow-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-[#2283FF]/30"
    >
      {/* Identity + meta */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14.5px] font-bold text-[#111827] truncate">
            {formatRequestTitle(request)}
          </span>
          {isClosed && (
            <span className="text-[10.5px] font-semibold text-gray-400 uppercase">Closed</span>
          )}
          {terms && (
            <span className="text-[11px] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded-full">
              Terms
            </span>
          )}
          {request.has_document && (
            <Paperclip className="w-3 h-3 text-[#2283FF]" aria-label="Document attached" />
          )}
        </div>
        {meta && <span className="text-[12px] text-gray-500 truncate block">{meta}</span>}
      </div>

      {/* Sent date + per-agent status chips */}
      <div className="hidden md:flex items-center gap-2 flex-none">
        {sent && <span className="text-[11px] text-gray-400">Sent {sent}</span>}
        <div className="flex items-center gap-1 flex-wrap max-w-[220px]">
          {recipients.slice(0, 4).map((r) => (
            <SearchRequestStatusChip key={r.id} status={r.status} />
          ))}
          {recipients.length > 4 && (
            <span className="text-[11px] text-gray-400">+{recipients.length - 4}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      {!isClosed && canClose && (
        <div className="relative flex-none" ref={menuRef}>
          <button
            type="button"
            aria-label="Request actions"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-8 bg-white py-1.5 shadow-lg border border-gray-100 rounded-lg z-10 min-w-[150px]">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  onClose(request);
                }}
                className="w-full text-left px-3 py-2 text-[13px] text-[#DC2626] hover:bg-[#FEF2F2]"
              >
                Close request
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchRequestListRow;
